import { Controller } from "@hotwired/stimulus"

// Block auto-save controller
// Automatically saves form changes and updates live preview
export default class extends Controller {
    static targets = ["form", "status"]
    static values = {
        url: String,
        blockId: String
    }

    connect() {
        console.log("[BlockAutosave] Connected", this.urlValue, this.blockIdValue)
        this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        this.saveTimeout = null
        this.isSaving = false
        this.lastSavedAt = 0

        // Ignore changes for first 500ms to avoid Alpine.js initialization events
        this.initialized = false
        setTimeout(() => {
            this.initialized = true
            console.log("[BlockAutosave] Ready for saves")
        }, 500)
    }

    // Called on any input change (debounced)
    save(event) {
        // Ignore source_type radio buttons (Alpine.js UI toggle, not actual data)
        if (event?.target?.name === 'source_type') {
            console.log("[BlockAutosave] Ignoring source_type change")
            return
        }

        // Ignore early events during initialization
        if (!this.initialized) {
            console.log("[BlockAutosave] Ignoring early event (not initialized)")
            return
        }

        // Don't save if a video upload is in progress (check for visible upload modal)
        if (this.isVideoUploadInProgress() || this.isImageUploadInProgress()) {
            console.log("[BlockAutosave] Ignoring - media upload in progress")
            return
        }

        console.log("[BlockAutosave] Save triggered by", event?.target?.name || "unknown")

        // Debounce saves
        if (this.saveTimeout) clearTimeout(this.saveTimeout)

        this.saveTimeout = setTimeout(() => {
            this.performSave()
        }, 500)
    }

    // Immediate save (for image/video uploads)
    saveNow(event) {
        console.log("[BlockAutosave] Immediate save triggered", event?.detail)
        if (this.saveTimeout) clearTimeout(this.saveTimeout)

        // Force initialized to true for immediate saves
        this.initialized = true
        this.performSave(true) // Process Turbo Stream to update live preview
    }

    // Check if video upload modal is visible
    isVideoUploadInProgress() {
        const uploadModal = document.querySelector('[data-video-upload-target="uploadModal"]')
        if (!uploadModal) return false
        return !uploadModal.classList.contains('hidden') && uploadModal.style.display !== 'none'
    }

    // Check if image upload is in progress
    isImageUploadInProgress() {
        // Look for any image upload controller in upload state within this block
        const uploadState = this.element.querySelector('[data-image-upload-target="uploadState"]')
        if (!uploadState) return false
        // If the upload state div is NOT hidden, then an upload is happening
        return !uploadState.classList.contains('hidden')
    }

    async performSave(processTurboStream = true) {
        if (this.isSaving) {
            console.log("[BlockAutosave] Already saving, skipping")
            return
        }

        // Prevent rapid-fire saves (at least 1 second between saves)
        const now = Date.now()
        if (now - this.lastSavedAt < 1000) {
            console.log("[BlockAutosave] Cooldown active, skipping")
            return
        }

        this.isSaving = true
        this.lastSavedAt = now

        this.showStatus("saving")

        try {
            // Get form data
            const form = this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
            if (!form) {
                console.error("[BlockAutosave] No form found!")
                this.showStatus("error")
                return
            }

            const formData = new FormData(form)

            console.log("[BlockAutosave] Saving to", this.urlValue)

            const response = await fetch(this.urlValue, {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': this.csrfToken,
                    'Accept': 'text/vnd.turbo-stream.html, text/html, application/json'
                },
                body: formData
            })

            if (response.ok) {
                const html = await response.text()

                // Only process the block preview replacement from Turbo Stream.
                // IMPORTANT: We must NOT replace the sidebar-content, because that
                // would re-initialize the autosave controller and cause a save loop.
                if (processTurboStream && html.includes('turbo-stream') && typeof Turbo !== 'undefined') {
                    // Filter: only apply turbo-stream actions that target the block preview,
                    // NOT the sidebar-content (which would destroy the current form)
                    const filteredHtml = this.filterTurboStreams(html)
                    if (filteredHtml) {
                        Turbo.renderStreamMessage(filteredHtml)
                    }
                }

                this.showStatus("saved")

            } else {
                const errorText = await response.text()
                console.error("[BlockAutosave] Save failed:", response.status, errorText.substring(0, 200))
                this.showStatus("error")
            }
        } catch (error) {
            console.error("[BlockAutosave] Save error:", error)
            this.showStatus("error")
        } finally {
            this.isSaving = false
        }
    }

    // Filter Turbo Stream responses to exclude sidebar-content updates
    // This prevents the save loop caused by sidebar form re-initialization
    filterTurboStreams(html) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
        const streams = doc.querySelectorAll('turbo-stream')

        let filtered = ''
        streams.forEach(stream => {
            const target = stream.getAttribute('target')
            // Skip sidebar-content updates to prevent form re-initialization loop
            if (target === 'sidebar-content') {
                console.log("[BlockAutosave] Skipping sidebar-content Turbo Stream")
                return
            }
            filtered += stream.outerHTML
        })

        return filtered || null
    }

    showStatus(status) {
        if (!this.hasStatusTarget) {
            console.log("[BlockAutosave] No status target, status:", status)
            return
        }

        const statusEl = this.statusTarget

        switch (status) {
            case "saving":
                statusEl.innerHTML = `<span class="flex items-center gap-1 text-gray-500">
          <svg class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Saving...
        </span>`
                break
            case "saved":
                statusEl.innerHTML = `<span class="flex items-center gap-1 text-green-600">
          <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
          Saved
        </span>`
                // Hide after 3 seconds
                setTimeout(() => { statusEl.innerHTML = "" }, 3000)
                break
            case "error":
                statusEl.innerHTML = `<span class="flex items-center gap-1 text-red-600">
          <svg class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          Error
        </span>`
                break
        }
    }

    disconnect() {
        if (this.saveTimeout) clearTimeout(this.saveTimeout)
    }
}
